/**
 * 2026-10-20 에디터 본문에 박힌 이미지 데이터(base64, `<img src="data:image/…">`)를 DB 에 넣지 않는다 — 단일 출처.
 *
 * ■ 왜
 *   웹페이지·문서에서 HTML 째 붙여넣으면 이미지가 파일이 아니라 `data:image/…;base64,…` 글자로 본문에 박혀
 *   글 한 건이 수 MB 가 됐다(운영 128행·64MB, 대부분 1:1 문의 — docs/EDITOR-PASTE-TW-CLEANUP.md §14).
 *
 * ■ 처리 (사용자 결정 2026-10-20: 처음엔 «거부»였다가 «업로드한 것처럼 자동으로 올려 주기»로 바꿨다)
 *   - 이미지를 올릴 수 있는 화면 → 붙여넣는 순간 파일로 올리고 그 주소로 바꾼다(툴바 업로드와 같은 테이블·필드·묶음)
 *   - 업로드를 막은 화면(튜터링·교육질문 등) · 올리기 실패 · 크기 초과 → ✕ 표시로 바꾸고 첨부로 넣게 안내
 *
 * ■ 쓰는 곳
 *   - `components/DYSummerNote.vue` onChange — 붙여넣은 data 이미지를 올리거나 ✕ 로 바꾼다
 *   - `composables/dy-summernote.js` getContents() — ✕ 표시와 아직 못 올린 data 이미지를 빼고 꺼내 준다(섬머노트를 쓰는 화면 전부:
 *     게시판 + 관리자앱 디자인·공지·메일 등 10곳. 그 화면들은 등록할 때 getContents() 만 부르므로 여기서 빼야 DB 에 안 들어간다)
 *   - `nuxt3-module-user-doyac/components/board/DYBoardEditor.vue` — CK에디터 업로드 어댑터(올리기는 CK 가 한다) /
 *     못 올리는 data 이미지 ✕ / 등록 직전 마지막 확인
 *
 * ■ ✕ 표시(REJECTED_IMAGE_MARK)는 편집 화면용이다 — 등록할 때 stripRejectedImageMarks() 로 빠진다.
 *   그래서 class 이름으로 찾는다. 이 이름을 바꾸면 이미 열려 있는 편집 화면의 표시가 저장돼 버린다.
 *
 * 🔴 의존성 없는 순수 모듈로 둔다(dy-summernote.js 같은 plain JS 에서도 import 한다).
 */

export const REJECTED_IMAGE_CLASS = 'dy-rejected-image'

export const REJECTED_IMAGE_NOTICE = '붙여넣은 이미지를 본문에 넣지 못했습니다. 이미지는 이미지 업로드(첨부)로 넣어 주세요. ✕ 표시는 등록할 때 빠집니다.'

export const REJECTED_IMAGE_TEXT = '✕ 이미지는 이미지 업로드(첨부)로 넣어 주세요'
export const REJECTED_IMAGE_STYLE = 'display:inline-block;padding:2px 8px;border:1px dashed #d32f2f;border-radius:4px;color:#d32f2f;font-size:13px;background:#fff5f5;'

/** ✕ 표시 HTML — 붙여넣은 이미지 하나가 이것 하나로 바뀐다 */
export const REJECTED_IMAGE_MARK = `<span class="${REJECTED_IMAGE_CLASS}" style="${REJECTED_IMAGE_STYLE}">${REJECTED_IMAGE_TEXT}</span>`

/** 섬머노트에서 올리는 중인 붙여넣은 이미지 표시(올리면 지운다). 저장 시점에 남아 있으면 «아직 못 올린 이미지»다 */
export const UPLOADING_IMAGE_ATTR = 'data-dy-uploading'

/** 붙여넣은 이미지를 자동으로 올리는 크기 상한 — 툴바 업로더(CommonDYUploader maxSizeMb 기본값)와 같게 */
export const PASTED_IMAGE_MAX_MB = 10

// 속성 순서·따옴표가 에디터마다 달라서(CK에디터는 다시 쓴다) src 만 보고 img 태그 통째로 잡는다
const DATA_IMAGE_TAG = /<img\b[^>]*?\ssrc\s*=\s*(["']?)\s*data:[^>]*>/gi
// 일반 회원 CK에디터는 마크다운 모드라 본문이 `![설명](data:image/…)` 로 들어온다(base64 에는 괄호·공백이 없다)
const DATA_IMAGE_MARKDOWN = /!\[[^\]]*\]\(\s*<?data:[^)\s>]*>?(?:\s+"[^"]*")?\s*\)/gi
const REJECTED_MARK_TAG = new RegExp(`<span\\b[^>]*\\b${REJECTED_IMAGE_CLASS}\\b[^>]*>[^<]*<\\/span>`, 'gi')

/**
 * 본문에 이미지 데이터가 남아 있는가 — img 의 data: 주소 + style 의 url(data:image…) + 마크다운 ![](data:image…) 까지.
 * (등록 직전 마지막 확인용. 화면에 있는 건 다 막는다)
 */
export function hasInlineImageData(html: string | null | undefined): boolean {
  if (!html) return false
  return /<img\b[^>]*?\ssrc\s*=\s*["']?\s*data:/i.test(html) || /data:image\/[a-z0-9.+-]+;base64,/i.test(html)
}

/** img 의 주소가 data: 인가 */
export function isInlineDataImage(img: Element): boolean {
  return /^\s*data:/i.test(img.getAttribute('src') ?? '')
}

/**
 * 본문 문자열의 data: 이미지를 ✕ 표시로 바꾼다 — HTML `<img>` 는 ✕ 표시 span 으로, 마크다운 `![](data:…)` 는 ✕ 글자로
 * (마크다운에 span 을 넣어도 CK에디터가 글자만 남긴다. 어느 쪽이든 등록할 때 stripRejectedImageMarks() 가 뺀다)
 */
export function rejectInlineImages(html: string): { html: string, count: number } {
  let count = 0
  const replaced = html
    .replace(DATA_IMAGE_TAG, () => {
      count++
      return REJECTED_IMAGE_MARK
    })
    .replace(DATA_IMAGE_MARKDOWN, () => {
      count++
      return REJECTED_IMAGE_TEXT
    })
  return { html: replaced, count }
}

/**
 * 본문 문자열에서 data: 이미지를 아예 뺀다 — 저장 직전 마지막 관문(섬머노트 getContents).
 * uploading = 그중 올리는 중이던 것(UPLOADING_IMAGE_ATTR)의 수 — 안내 문구를 가르는 데 쓴다
 */
export function removeInlineImages(html: string): { html: string, count: number, uploading: number } {
  let count = 0
  let uploading = 0
  const replaced = html.replace(DATA_IMAGE_TAG, (tag) => {
    count++
    if (tag.indexOf(UPLOADING_IMAGE_ATTR) !== -1) uploading++
    return ''
  })
  return { html: replaced, count, uploading }
}

/** 이미지 하나를 ✕ 표시로 바꾼다 — 섬머노트처럼 DOM 을 직접 다루는 에디터용 */
export function rejectImageElement(img: Element) {
  const holder = img.ownerDocument.createElement('span')
  holder.innerHTML = REJECTED_IMAGE_MARK
  const mark = holder.firstChild as HTMLElement
  mark.setAttribute('contenteditable', 'false') // 섬머노트에서 표시 글자 안으로 커서가 들어가 글이 섞이지 않게(CK에디터는 자체 모델이라 붙이지 않는다)
  img.replaceWith(mark)
}

/** 편집 화면(DOM) 안의 data: 이미지를 ✕ 표시로 바꾼다 — 이미지를 올릴 수 없는 화면용 */
export function rejectInlineImagesInElement(root: Element | null | undefined): number {
  if (!root) return 0
  const images = Array.from(root.querySelectorAll('img')).filter(isInlineDataImage)
  images.forEach(rejectImageElement)
  return images.length
}

/**
 * data: 주소를 올릴 수 있는 파일로 바꾼다. 이미지가 아니거나 깨진 데이터면 null.
 * 🔴 끝 패딩(=)이 없는 base64 가 실제로 있다(운영 이관 때 확인) — atob 는 패딩 없이도 받는다(forgiving-base64)
 */
export function dataUrlToFile(dataUrl: string, baseName = 'pasted-image'): File | null {
  const comma = dataUrl.indexOf(',')
  if (comma === -1) return null
  const header = dataUrl.slice(0, comma).trim()
  const body = dataUrl.slice(comma + 1)
  const mime = (/^data:([^;,]+)/i.exec(header)?.[1] ?? '').trim().toLowerCase()
  if (!mime.startsWith('image/')) return null

  let bytes: Uint8Array
  try {
    if (/;base64$/i.test(header)) {
      const binary = atob(decodeURIComponent(body).replace(/\s+/g, ''))
      bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0))
    } else {
      bytes = new TextEncoder().encode(decodeURIComponent(body)) // data:image/svg+xml,<svg…> 처럼 글자 그대로 담긴 것
    }
  } catch {
    return null
  }
  if (!bytes.length) return null

  const ext = (mime.split('/')[1] ?? 'png').replace(/\+.*$/, '').replace('jpeg', 'jpg') // svg+xml → svg
  return new File([bytes], `${baseName}.${ext}`, { type: mime })
}

/**
 * 등록 직전 ✕ 표시를 뺀다. 마크다운 모드 CK에디터는 span 을 버리고 글자만 남기므로 글자도 지운다.
 * (빈 값은 그대로 돌려준다 — `getContents() ?? ''` 처럼 쓰는 호출부가 있다)
 */
export function stripRejectedImageMarks<T extends string | null | undefined>(html: T): T {
  if (!html) return html
  if (html.indexOf(REJECTED_IMAGE_CLASS) === -1 && html.indexOf(REJECTED_IMAGE_TEXT) === -1) return html
  return html.replace(REJECTED_MARK_TAG, '').split(REJECTED_IMAGE_TEXT).join('') as T
}
